import { formatMoney, type Currency } from "@talentkontor/domain";
import { ApiError } from "../../data/api";

/**
 * Media-Kit und Pitch-Liste als Link (0074): Antwort der Datenbank (app.media_kit_payload) in eine Ansicht, Zustände des Links
 * und Texte in Deutsch und Englisch. Rein, ohne Datenbank; genutzt von /kit/<Schlüssel> und der Vorschau im Agentur-Bereich.
 */

export type KitLang = "de" | "en";

export interface KitChannel {
  platform: string;
  handle?: string | null;
  followers?: number | null;
  avg_views?: number | null;
  engagement_bp?: number | null;
  as_of?: string | null;
  /** Aufrufe der letzten 30 Tage und Story-Aufrufe, von Hand (0214) */
  views_30d?: number | null;
  story_views?: number | null;
}
/** Zielgruppe von Hand (0214): Anteile 0 bis 1 wie bei Instagram, dazu der Stand */
export interface KitAudience { as_of: string | null; gender?: Record<string, number>; age?: Record<string, number>; country?: Record<string, number> }
export interface KitInstagram {
  blocks: string[];
  as_of: string | null;
  profile: { username: string; name: string | null; followers: number | null; media_count: number | null } | null;
  period_totals: Record<string, number | null> | null;
  audience: Record<string, Record<string, number>> | null;
}
export interface KitTalent {
  talent_id: string;
  name: string;
  has_image: boolean;
  bio_de: string | null;
  bio_en: string | null;
  topics: string[];
  /** Themen auf Englisch (0081); leer = die deutschen Themen gelten auch im englischen Link */
  topics_en: string[];
  /** Rollenzeile unter dem Namen (0214), z. B. „Creatorin | Sportlerin“ */
  roles_de: string | null;
  roles_en: string | null;
  audience: KitAudience | null;
  /** Fotos der Collage und Marken-Logos (0214): Kennungen, die Bilder lädt die Seite einzeln */
  photos: string[];
  /** Bildausschnitt je Foto (0217): oben, unten, links, rechts; fehlt = Mitte */
  photoFocus: Record<string, string>;
  logos: { id: string; label: string | null }[];
  handles: Record<string, string>;
  channels: KitChannel[];
  instagram: KitInstagram | null;
  references: { brand: string | null; platforms: string[] | null; year: number | null; note: string | null }[] | null;
  rates: { label: string; label_en: string | null; amount_cents: number; currency: string }[] | null;
}
export interface KitView {
  kind: "single" | "pitch";
  title: string | null;
  language: KitLang;
  expiresAt: string;
  /** Zeitpunkt, zu dem die Antwort erstellt wurde (0214): steht auf der Seite und im PDF */
  createdAt: string | null;
  brand: string | null;
  message: string | null;
  agency: { name: string; displayName: string; logoPath: string | null; brandColor: string | null; email: string | null };
  talents: KitTalent[];
}

export type KitOutcome =
  | { kind: "open"; view: KitView }
  | { kind: "wrong"; remaining: number }
  | { kind: "revoked" }
  /** 0425 (S-7): Vertrag der Agentur beendet */
  | { kind: "ended" }
  | { kind: "expired" }
  | { kind: "locked" }
  | { kind: "unavailable" }
  | { kind: "invalid" }
  | { kind: "failed" };

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);

function anteile(a: Record<string, unknown>): Pick<KitAudience, "gender" | "age" | "country"> {
  const out: Pick<KitAudience, "gender" | "age" | "country"> = {};
  for (const k of ["gender", "age", "country"] as const) {
    const v = a[k];
    if (isRecord(v)) out[k] = Object.fromEntries(Object.entries(v).filter((e): e is [string, number] => typeof e[1] === "number" && Number.isFinite(e[1])));
  }
  return out;
}

/**
 * Absender gegenüber Marken (Inhaber 24.10.2026): der Firmenname (Rechtsträger aus den Firmendaten, org_settings.legal_name),
 * wenn er gesetzt ist, sonst der Anzeigename der Organisation. app.media_kit_payload liefert das bereits als agency.name
 * (coalesce(legal_name, organizations.name)), agency.display_name ist immer der Anzeigename.
 */
export function kitAbsender(view: Pick<KitView, "agency">): string {
  return view.agency.name.trim() || view.agency.displayName.trim();
}

/**
 * „Zusammengestellt von …“ unter dem Blatt bzw. im PDF-Stempel: nur, wenn der Kopf ein Logo zeigt. Steht im Kopf schon der
 * Firmenname als Text, entfällt der Satz (sonst stünde die Firma zweimal da).
 */
export function kitZusammengestellt(view: Pick<KitView, "agency">, lang: KitLang, logoImKopf: boolean): string | null {
  const name = kitAbsender(view);
  if (!logoImKopf || !name) return null;
  return lang === "en" ? `Compiled by ${name}` : `Zusammengestellt von ${name}`;
}

export function kitTokenLooksValid(token: string): boolean {
  return /^[A-Za-z0-9_-]{32,200}$/.test(token);
}

/** Antwort in eine Ansicht; fehlende oder falsche Felder werden leer statt die Seite zu brechen. */
export function buildKitView(raw: unknown): KitView | null {
  if (!isRecord(raw) || raw.ok !== true) return null;
  const a = isRecord(raw.agency) ? raw.agency : {};
  const talents = Array.isArray(raw.talents) ? raw.talents.filter(isRecord) : [];
  return {
    kind: raw.kind === "pitch" ? "pitch" : "single",
    title: str(raw.title),
    language: raw.language === "en" ? "en" : "de",
    expiresAt: str(raw.expires_at) ?? "",
    createdAt: str(raw.created_at),
    brand: str(raw.brand),
    message: str(raw.message),
    agency: { name: str(a.name) ?? "", displayName: str(a.display_name) ?? str(a.name) ?? "", logoPath: str(a.logo_path), brandColor: str(a.brand_color), email: str(a.email) },
    talents: talents.map((t) => ({
      talent_id: String(t.talent_id ?? ""),
      name: str(t.name) ?? "",
      has_image: t.has_image === true,
      bio_de: str(t.bio_de),
      bio_en: str(t.bio_en),
      topics: Array.isArray(t.topics) ? t.topics.filter((x): x is string => typeof x === "string") : [],
      topics_en: Array.isArray(t.topics_en) ? t.topics_en.filter((x): x is string => typeof x === "string") : [],
      roles_de: str(t.roles_de),
      roles_en: str(t.roles_en),
      audience: isRecord(t.audience) ? { as_of: str(t.audience.as_of), ...anteile(t.audience) } : null,
      photos: Array.isArray(t.photos) ? t.photos.map((x) => (typeof x === "string" ? x : isRecord(x) && typeof x.id === "string" ? x.id : "")).filter(Boolean) : [],
      photoFocus: Array.isArray(t.photos) ? Object.fromEntries(t.photos.filter(isRecord).filter((x) => typeof x.id === "string" && typeof x.focus === "string").map((x) => [String(x.id), String(x.focus)])) : {},
      logos: Array.isArray(t.logos) ? t.logos.filter(isRecord).map((l) => ({ id: String(l.id ?? ""), label: str(l.label) })).filter((l) => l.id) : [],
      handles: isRecord(t.handles) ? Object.fromEntries(Object.entries(t.handles).filter(([, v]) => typeof v === "string")) as Record<string, string> : {},
      channels: Array.isArray(t.channels) ? t.channels.filter(isRecord).map((c) => ({
        platform: String(c.platform ?? "other"), handle: str(c.handle), followers: num(c.followers), avg_views: num(c.avg_views), engagement_bp: num(c.engagement_bp), as_of: str(c.as_of),
        views_30d: num(c.views_30d), story_views: num(c.story_views),
      })) : [],
      instagram: isRecord(t.instagram) ? {
        blocks: Array.isArray(t.instagram.blocks) ? t.instagram.blocks.filter((x): x is string => typeof x === "string") : [],
        as_of: str(t.instagram.as_of),
        profile: isRecord(t.instagram.profile) ? { username: String(t.instagram.profile.username ?? ""), name: str(t.instagram.profile.name), followers: num(t.instagram.profile.followers), media_count: num(t.instagram.profile.media_count) } : null,
        period_totals: isRecord(t.instagram.period_totals) ? Object.fromEntries(Object.entries(t.instagram.period_totals).map(([k, v]) => [k, num(v)])) : null,
        audience: isRecord(t.instagram.audience) ? t.instagram.audience as Record<string, Record<string, number>> : null,
      } : null,
      references: Array.isArray(t.references) ? t.references.filter(isRecord).map((r) => ({
        brand: str(r.brand), platforms: Array.isArray(r.platforms) ? r.platforms.filter((x): x is string => typeof x === "string") : null, year: num(r.year), note: str(r.note),
      })) : null,
      rates: Array.isArray(t.rates) ? t.rates.filter(isRecord).map((r) => ({ label: String(r.label ?? ""), label_en: str(r.label_en), amount_cents: num(r.amount_cents) ?? 0, currency: String(r.currency ?? "EUR") })) : null,
    })),
  };
}

export function kitOutcomeFromResponse(r: unknown): KitOutcome {
  if (!isRecord(r)) return { kind: "failed" };
  if (r.ok === true) {
    const view = buildKitView(r);
    return view ? { kind: "open", view } : { kind: "failed" };
  }
  const remaining = num(r.remaining) ?? 0;
  return remaining <= 0 ? { kind: "locked" } : { kind: "wrong", remaining };
}

/** Abgelehnt mit Grund (kit:revoked, kit:expired, kit:locked, kit:unavailable, kit:invalid). */
export function kitOutcomeFromError(e: unknown): KitOutcome {
  const hint = e instanceof ApiError ? e.hint : isRecord(e) && typeof e.hint === "string" ? e.hint : undefined;
  if (hint === "link:ended") return { kind: "ended" };
  if (hint === "kit:revoked") return { kind: "revoked" };
  if (hint === "kit:expired") return { kind: "expired" };
  if (hint === "kit:locked") return { kind: "locked" };
  if (hint === "kit:unavailable") return { kind: "unavailable" };
  if (hint === "kit:invalid") return { kind: "invalid" };
  const message = e instanceof Error ? e.message : String(e);
  if (/Ungültiger Link/i.test(message)) return { kind: "invalid" };
  if (/zurückgezogen/i.test(message)) return { kind: "revoked" };
  if (/abgelaufen/i.test(message)) return { kind: "expired" };
  if (/gesperrt|Fehlversuche/i.test(message)) return { kind: "locked" };
  return { kind: "failed" };
}

// ── Texte und Zahlen ───────────────────────────────────────────────────────────

export const tk = (lang: KitLang) => (de: string, en: string) => (lang === "en" ? en : de);

/** Sprache vor dem Öffnen: ?l=en bzw. ?l=de aus dem Link (Mail, Kopie im Team), sonst die Browsersprache (Deutsch bei „de…“). */
export function kitLangVorab(search: string, browser: string | undefined): KitLang {
  const l = new URLSearchParams(search).get("l");
  if (l === "de" || l === "en") return l;
  return browser && !/^de\b/i.test(browser) ? "en" : "de";
}

const PLATFORM_NAMES: Record<string, [string, string]> = {
  instagram: ["Instagram", "Instagram"], tiktok: ["TikTok", "TikTok"], youtube: ["YouTube", "YouTube"], twitch: ["Twitch", "Twitch"],
  linkedin: ["LinkedIn", "LinkedIn"], pinterest: ["Pinterest", "Pinterest"], snapchat: ["Snapchat", "Snapchat"], facebook: ["Facebook", "Facebook"],
  x: ["X", "X"], podcast: ["Podcast", "Podcast"], blog: ["Blog", "Blog"], other: ["Sonstige", "Other"],
};
export function platformName(key: string, lang: KitLang): string {
  const p = PLATFORM_NAMES[key];
  return p ? p[lang === "en" ? 1 : 0] : key;
}

/** Kurze Zahl: 48.200 → „48,2 Tsd.“ bzw. „48.2K“; unter 10.000 ausgeschrieben. */
export function compact(n: number | null | undefined, lang: KitLang): string {
  if (n == null) return "–";
  const locale = lang === "en" ? "en-GB" : "de-DE";
  if (Math.abs(n) < 10000) return new Intl.NumberFormat(locale).format(n);
  return new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 }).format(n);
}
export function percentOfBp(bp: number | null | undefined, lang: KitLang): string {
  if (bp == null) return "–";
  return `${new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 2 }).format(bp / 100)} %`;
}
export function kitDate(iso: string | null | undefined, lang: KitLang): string {
  if (!iso) return "–";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(d);
}
export function kitMoney(cents: number, currency: string, lang: KitLang): string {
  // Im Kit eine Schreibweise je Sprache, auch bei gemischten Währungen („1.500,00 €“ und „600,00 CHF“, Praxistest K-032)
  return formatMoney(Math.round(cents), currency as Currency, lang === "en" ? "en-GB" : "de-DE");
}

/** Bio in der gewählten Sprache, sonst die andere (mit Hinweis). */
export function bioFor(t: Pick<KitTalent, "bio_de" | "bio_en">, lang: KitLang): { text: string | null; fallback: boolean } {
  const own = lang === "en" ? t.bio_en : t.bio_de;
  if (own) return { text: own, fallback: false };
  const other = lang === "en" ? t.bio_de : t.bio_en;
  return { text: other, fallback: !!other };
}

/** Rollenzeile in der Sprache des Links; ohne englische Fassung die deutsche. */
export function rolesFor(t: Pick<KitTalent, "roles_de" | "roles_en">, lang: KitLang): string | null {
  return (lang === "en" ? t.roles_en ?? t.roles_de : t.roles_de ?? t.roles_en) ?? null;
}
/** Zeitpunkt mit Uhrzeit für „Erstellt am“ (Zone des Lesers). */
export function kitStamp(iso: string | null | undefined, lang: KitLang): string {
  const d = iso ? new Date(iso) : new Date();
  const tag = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
  const zeit = new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { hour: "2-digit", minute: "2-digit" }).format(d);
  return lang === "en" ? `${tag}, ${zeit}` : `${tag}, ${zeit} Uhr`;
}
/** Adresse eines Kontos auf der Plattform (nur bekannte Plattformen, nur einfache Namen), sonst null. */
export function handleUrl(platform: string, handle: string | null | undefined): string | null {
  const h = (handle ?? "").trim().replace(/^@/, "");
  if (!/^[A-Za-z0-9._-]{1,60}$/.test(h)) return null;
  const basis: Record<string, string> = { instagram: "https://www.instagram.com/", tiktok: "https://www.tiktok.com/@", youtube: "https://www.youtube.com/@", twitch: "https://www.twitch.tv/",
    pinterest: "https://www.pinterest.com/", x: "https://x.com/", snapchat: "https://www.snapchat.com/add/" };
  return basis[platform] ? `${basis[platform]}${h}` : null;
}
/** Kurze Zahl einheitlich in beiden Sprachen (Vorbild: „40 K“, „1,5 M“ bzw. „1.5M“). */
export function kurz(n: number | null | undefined, lang: KitLang): string {
  if (n == null) return "–";
  const f = (v: number, d: number) => new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: d }).format(v);
  if (Math.abs(n) >= 1_000_000) return `${f(n / 1_000_000, 1)} M`;
  if (Math.abs(n) >= 10_000) return `${f(n / 1000, 1)} K`;
  return f(n, 0);
}

/** Themen in der Sprache des Links; ohne englische Themen gelten die deutschen (SICHT-AGENTUR-REST-4). */
export function topicsFor(t: Pick<KitTalent, "topics" | "topics_en">, lang: KitLang): string[] {
  return lang === "en" && t.topics_en.length > 0 ? t.topics_en : t.topics;
}
/** Leistung eines Preises in der Sprache des Links; ohne englische Bezeichnung die deutsche. */
export function rateLabel(r: { label: string; label_en?: string | null }, lang: KitLang): string {
  return lang === "en" && r.label_en?.trim() ? r.label_en.trim() : r.label;
}

/** Größte Anteile einer Zielgruppen-Aufteilung, absteigend (höchstens n). */
export function topShares(data: Record<string, number> | undefined, n = 3): { key: string; share: number }[] {
  if (!data) return [];
  return Object.entries(data).filter(([, v]) => typeof v === "number").sort((a, b) => b[1] - a[1]).slice(0, n).map(([key, share]) => ({ key, share }));
}

/** Summen einer Pitch-Liste: Creator und Follower über alle Plattformen (Instagram verbunden geht der Angabe von Hand vor). */
export function kitSummary(v: KitView): { creators: number; followers: number } {
  let followers = 0;
  for (const t of v.talents) {
    const ig = t.instagram?.profile?.followers ?? null;
    followers += (ig ?? 0) + t.channels.reduce((s, c) => s + (c.platform === "instagram" && ig != null ? 0 : c.followers ?? 0), 0);
  }
  return { creators: v.talents.length, followers };
}

// ── Kennzahlen je Plattform als Spalten (0214) ───────────────────────────────────

export interface KitSpalte {
  platform: string; handle: string | null; followers: number | null; views30: number | null; reach30: number | null; engagementBp: number | null;
  avgViews: number | null; storyViews: number | null; asOf: string | null;
  /** true = Werte aus dem verbundenen Instagram-Konto, sonst Angabe der Agentur */
  connected: boolean;
}
/** Spalten der Kennzahlen-Tabelle: Kanäle von Hand, Instagram aus dem verbundenen Konto geht vor. Höchstens vier. */
export function kitSpalten(c: Pick<KitTalent, "channels" | "instagram">): KitSpalte[] {
  const out: KitSpalte[] = c.channels.map((ch) => ({
    platform: ch.platform, handle: ch.handle ?? null, followers: ch.followers ?? null, views30: ch.views_30d ?? null, reach30: null, engagementBp: ch.engagement_bp ?? null,
    avgViews: ch.avg_views ?? null, storyViews: ch.story_views ?? null, asOf: ch.as_of ?? null, connected: false,
  }));
  const ig = c.instagram;
  const tot = ig?.period_totals ?? {};
  if (ig && (ig.profile?.followers != null || tot.views != null || tot.reach != null)) {
    let s = out.find((x) => x.platform === "instagram");
    if (!s) {
      s = { platform: "instagram", handle: null, followers: null, views30: null, reach30: null, engagementBp: null, avgViews: null, storyViews: null, asOf: null, connected: true };
      out.unshift(s);
    }
    s.connected = true;
    s.handle = ig.profile?.username ? `@${ig.profile.username}` : s.handle;
    s.followers = ig.profile?.followers ?? s.followers;
    s.views30 = tot.views ?? s.views30;
    s.reach30 = tot.reach ?? null;
    s.asOf = ig.as_of ?? s.asOf;
  }
  return out.slice(0, 4);
}
export interface KitZielgruppe { asOf: string | null; connected: boolean; gender: { key: string; share: number }[]; age: { key: string; share: number }[]; country: { key: string; share: number }[] }
/** Zielgruppe: aus dem verbundenen Instagram-Konto, sonst von Hand; null, wenn nichts da ist. */
export function kitZielgruppe(c: Pick<KitTalent, "audience" | "instagram">): KitZielgruppe | null {
  const ig = c.instagram?.audience ?? null;
  const hatIg = !!ig && ["age", "gender", "country"].some((k) => ig[k] && Object.keys(ig[k]!).length > 0);
  const q = hatIg ? ig! : c.audience ? (c.audience as unknown as Record<string, Record<string, number>>) : null;
  if (!q) return null;
  const z: KitZielgruppe = { asOf: hatIg ? c.instagram?.as_of ?? null : c.audience?.as_of ?? null, connected: hatIg,
    gender: topShares(q.gender, 3), age: topShares(q.age, 4), country: topShares(q.country, 4) };
  return z.gender.length || z.age.length || z.country.length ? z : null;
}
export function genderName(k: string, lang: KitLang): string {
  return k === "f" ? (lang === "en" ? "Female" : "Weiblich") : k === "m" ? (lang === "en" ? "Male" : "Männlich") : lang === "en" ? "Other" : "Divers";
}
export function anteil(v: number, lang: KitLang): string {
  return `${new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 1 }).format(v * 100)} %`;
}

/** Bildausschnitt als Anteile (x, y von 0 bis 1) für CSS object-position und den Zuschnitt im PDF. */
export function fokusPunkt(focus: string | null | undefined): { x: number; y: number } {
  switch (focus) {
    case "oben": return { x: 0.5, y: 0 };
    case "unten": return { x: 0.5, y: 1 };
    case "links": return { x: 0, y: 0.5 };
    case "rechts": return { x: 1, y: 0.5 };
    default: return { x: 0.5, y: 0.5 };
  }
}
/**
 * Maße eines Logos, damit alle gleich schwer wirken: gleiche Fläche statt gleicher Höhe, mit Grenzen. Ein quadratisches
 * Zeichen wird dadurch nicht riesig und ein sehr breiter Schriftzug nicht winzig (Praxistest K-051, K-052).
 */
export function logoMass(breite: number, hoehe: number, basis = 64, maxB = 190, maxH = 46): { w: number; h: number } {
  const r = breite > 0 && hoehe > 0 ? breite / hoehe : 3;
  let w = basis * Math.sqrt(r), h = basis / Math.sqrt(r);
  if (h > maxH) { w *= maxH / h; h = maxH; }
  if (w > maxB) { h *= maxB / w; w = maxB; }
  return { w: Math.round(w * 10) / 10, h: Math.round(h * 10) / 10 };
}

/** Ansicht ohne Preise: für Teammitglieder ohne das Recht „Beträge sehen“ (Praxistest K-037). */
export function ohnePreise(v: KitView): KitView {
  return { ...v, talents: v.talents.map((x) => ({ ...x, rates: null })) };
}
